import { useEffect, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Alert, Autocomplete, Divider, Chip } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Countries } from "../../../utils/Lookup";
import { GroupHeader, GroupItems } from "../../../utils/Styled";

import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

export default function EditTouchpointForm({ open, handleClose, rowData, organizationData, fetchData }) {
    const [editData, setEditData] = useState({
        touchpoint_id: null,
        org_id: "",
        org_name: "",
        email: "",
        office_phone: "",
        mobile_phone: "",
        pic_name: "",
        product_id: "",
        contact_date: null,
        reminder_date: null,
        is_assigned_to_group: 0,
        assigned_to: "",
        remark: "",
    });
    const [errors, setErrors] = useState({ contact_date: false, assigned_to: false, remark: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const [productsData, setProductsData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [selectedProductValue, setSelectedProductValue] = useState(null);
    const [selectedUserValue, setSelectedUserValue] = useState(null);

    useEffect(() => {
        if (open && rowData) {
            setEditData({
                touchpoint_id: rowData.touchpoint_id,
                org_id: rowData.org_id || organizationData?.org_id || "",
                org_name: rowData.org_name || organizationData?.org_name || "",
                email: rowData.email || organizationData?.email || "",
                office_phone: rowData.office_phone || organizationData?.office_phone || "",
                mobile_phone: rowData.mobile_phone || organizationData?.mobile_phone || "",
                pic_name: rowData.pic_name || organizationData?.pic_name || "",
                product_id: rowData.product_id || "",
                contact_date: rowData.contact_date ? moment(rowData.contact_date).toDate() : null,
                reminder_date: rowData.reminder_date ? moment(rowData.reminder_date).toDate() : null,
                is_assigned_to_group: rowData.is_assigned_to_group !== undefined ? rowData.is_assigned_to_group : 0,
                assigned_to: rowData.assigned_to || "",
                remark: rowData.remark || "",
            });

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/select")
                .then((response) => {
                    setProductsData(response.data.data.products);
                    setSelectedProductValue(response.data.data.products.find((option) => option.product_id === rowData.product_id) || null);
                })
                .catch((error) => console.log(error));

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/users/groups/select")
                .then((response) => {
                    setUsersData(response.data.data.users);
                    setSelectedUserValue(response.data.data.users.find((option) => option.id === rowData.assigned_to && option.is_group === rowData.is_assigned_to_group) || null);
                })
                .catch((error) => console.log(error));

            setErrors({ contact_date: false, assigned_to: false, remark: false });
            setHelperText({});
            setErrorMessage("");
        }
    }, [open, rowData, organizationData]);

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "contact_date" && (value === "" || value === null)) {
            await setError("contact_date", "Contact Date is required.");
        } else if (field === "assigned_to" && value === "") {
            await setError("assigned_to", "Assigned To is required.");
        } else if (field === "remark" && (value || "").trim() === "") {
            await setError("remark", "Remark is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const updateTouchpoint = async (markAsCompleted = false) => {
        let isValid = true;

        if (!editData.contact_date) isValid = false;
        if (!editData.assigned_to) isValid = false;
        if (!editData.remark || editData.remark.trim() === "") isValid = false;

        await validate("contact_date", editData.contact_date);
        await validate("assigned_to", editData.assigned_to);
        await validate("remark", editData.remark);

        if (!isValid) {
            setErrorMessage("Please fill all the required fields.");
            return;
        }

        try {
            setErrorMessage("");

            const payload = {
                product_id: editData.product_id ? editData.product_id : "",
                contact_date: moment(editData.contact_date).format("YYYY-MM-DD"),
                reminder_date: moment(editData.reminder_date).isValid() ? moment(editData.reminder_date).format("YYYY-MM-DD") : "",
                is_assigned_to_group: editData.is_assigned_to_group,
                assigned_to: editData.assigned_to,
                remark: editData.remark.trim(),
            };

            if (markAsCompleted) {
                payload.is_completed = 1;
            }

            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/touchpoints/${rowData.touchpoint_id}`, payload);

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: markAsCompleted ? "Touchpoint marked as called/followed successfully" : "Touchpoint information updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the touchpoint information. Please try again later.");
            }
        }
    };

    const handleSave = async () => {
        await updateTouchpoint(false);
    };

    const handleMarkAsCompleted = async () => {
        const confirmation = await Swal.fire({
            title: "Mark as Called / Followed?",
            text: "Are you sure you want to mark this touchpoint as called/followed?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            reverseButtons: true,
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        await updateTouchpoint(true);
    };

    if (!rowData) return null;

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Touchpoint Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Touchpoint Details" />
                </Divider>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField margin="dense" label="Organization" fullWidth value={editData.org_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Email" fullWidth value={editData.email} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Office Phone" fullWidth value={editData.office_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Mobile Phone" fullWidth value={editData.mobile_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="PIC Name" fullWidth value={editData.pic_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={productsData}
                            groupBy={(option) => {
                                const country = Countries.find((country) => country.id === option.country_id);
                                return country ? country.name : "Unknown";
                            }}
                            getOptionLabel={(option) => (option ? option.product_description : "Select product")}
                            onChange={(event, value) => {
                                setSelectedProductValue(value);
                                setEditData({ ...editData, product_id: value ? value.product_id : "" });
                            }}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.product_id}>
                                        {option.product_description}
                                    </li>
                                );
                            }}
                            isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                            value={selectedProductValue}
                            renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth />}
                            renderGroup={(params) => {
                                return (
                                    <li key={params.key}>
                                        <GroupHeader>{params.group}</GroupHeader>
                                        <GroupItems>{params.children}</GroupItems>
                                    </li>
                                );
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Contact Date"
                                inputFormat="YYYY-MM-DD"
                                value={editData.contact_date}
                                onChange={(newValue) => {
                                    setEditData({ ...editData, contact_date: newValue });
                                    validate("contact_date", newValue);
                                }}
                                renderInput={(params) => <TextField {...params} required fullWidth margin="dense" error={errors.contact_date} helperText={helperText.contact_date} />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Reminder Date"
                                inputFormat="YYYY-MM-DD"
                                value={editData.reminder_date}
                                onChange={(newValue) => {
                                    setEditData({ ...editData, reminder_date: newValue });
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth margin="dense" />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            options={usersData}
                            getOptionLabel={(option) => (option ? option.name : "Select user or group")}
                            groupBy={(option) => option.is_group}
                            onChange={(event, value) => {
                                setSelectedUserValue(value);
                                setEditData({ ...editData, assigned_to: value ? value.id : "", is_assigned_to_group: value ? value.is_group : 0 });
                                validate("assigned_to", value ? value.id : "");
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id && option.is_group === value.is_group}
                            value={selectedUserValue}
                            renderInput={(params) => <TextField {...params} margin="dense" id="assigned_to" label="Assigned To" fullWidth required error={errors.assigned_to} helperText={helperText.assigned_to} />}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <GroupHeader>{params.group === 1 ? "Groups" : "Users"}</GroupHeader>
                                    <GroupItems>{params.children}</GroupItems>
                                </li>
                            )}
                        />
                    </Grid>
                </Grid>
                {/* <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Additional Details" />
                </Divider> */}
                <TextField
                    autoComplete="off"
                    margin="dense"
                    label="Remark"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={editData.remark}
                    error={errors.remark}
                    helperText={helperText.remark}
                    onChange={(e) => {
                        setEditData({ ...editData, remark: e.target.value });
                        validate("remark", e.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleMarkAsCompleted} variant="contained" color="success" style={{ marginRight: "6px", color: "white" }}>
                    Called / Followed
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Save
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
